import {
  ActionIcon,
  Card,
  Group,
  Stack,
  Table,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from "@mantine/core";
import { openContextModal } from "@mantine/modals";
import { IconFlag, IconPencil, IconTrophy } from "@tabler/icons-react";
import { useTranslation } from "next-i18next";
import { CircleFlag } from "react-circle-flags";
import { trackEvent } from "../../common/analytics";
import {
  useBoard,
  useIsBoardAdmin,
  useIsBoardReferee,
} from "../../common/boards/board.hooks";
import { Event } from "../../common/events/event.schema";
import { Member } from "../../common/members/member.schema";
import { getShowClubs, getShowCountries } from "../../common/util";
import Hider from "../common/Hider";
import RecordAvatar from "../common/RecordAvatar";
import MemberDisplay from "../members/MemberDisplay";
import PrintResults from "./PrintResults";
export const ResultRow = ({
  memberId,
  position,
  retired,
  event,
  showClub,
  showFlag,
  noLinks,
}: {
  memberId: string;
  position: number;
  retired?: boolean;
  showClub?: boolean;
  showFlag?: boolean;
  event: Event;
  noLinks?: boolean;
}) => {
  // const { member } = useMemberById(memberId);
  const { t } = useTranslation();
  const openEditTeam = (team: Member) => {
    // console.log("openEditTeam :", team);
    trackEvent("open_modal_edit_team", { board: event.board });
    openContextModal({
      title: t("Edit team"),
      modal: "editTeam",
      innerProps: {
        team,
      },
    });
  };
  const editable = useIsBoardAdmin();
  let member: Member | undefined;
  if (event.teams.length > 0) {
    member = (event.teams as Member[]).find((m) => m._id === memberId);
  } else {
    member = (event.members as Member[]).find((m) => m._id === memberId);
  }
  const { board } = useBoard();
  const colorScheme = useComputedColorScheme();
  if (!member || !board) return <></>;
  return (
    <Table.Tr key={member._id} style={{ pageBreakInside: "avoid" }}>
      <Table.Td>{position}</Table.Td>
      <Table.Td>
        <Group>
          <RecordAvatar record={member} />
          <Stack py={member.team ? 10 : 0} gap={0}>
            <Group>
              <MemberDisplay member={member} link={!member.team && !noLinks} />
              {editable && member.team && (
                <ActionIcon
                  onClick={() => member && openEditTeam(member)}
                  color="primary"
                >
                  <IconPencil size={14} />
                </ActionIcon>
              )}
            </Group>

            {member.team && (
              <Stack mt={10}>
                {(member.members as Member[])
                  ?.sort((a, b) => b.score - a.score)
                  .map((member, index) => {
                    return (
                      <MemberDisplay
                        avatar
                        size="xs"
                        key={member._id || index}
                        member={member}
                        link={!noLinks}
                      />
                    );
                  })}
              </Stack>
            )}
            {showClub && member.club && (
              <Hider largerThan="sm">
                <Text size="xs">{showClub && member.club}</Text>
              </Hider>
            )}
          </Stack>
        </Group>
      </Table.Td>
      <Hider smallerThan="sm">
        <Table.Td>
          <Text size="xs">{showClub && member.club}</Text>
        </Table.Td>
      </Hider>

      {showFlag && (
        <Table.Td>
          {member.country && (
            <CircleFlag
              countryCode={member.country.toLowerCase()}
              height={20}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group>
          {position === 1 && (
            <ThemeIcon color="yellow" variant="light">
              <IconTrophy />
            </ThemeIcon>
          )}
          {position === 2 && (
            <ThemeIcon color="gray" variant="light">
              <IconTrophy />
            </ThemeIcon>
          )}
          {(position === 3 || (!event.playoff && position === 4)) && (
            <ThemeIcon color="orange" variant="light">
              <IconTrophy />
            </ThemeIcon>
          )}
          {retired && (
            <ThemeIcon
              color={colorScheme === "dark" ? "gray" : "gray"}
              variant="light"
            >
              <IconFlag />
            </ThemeIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
const Results = ({
  event,
  noLinks,
  noPrint,
  max,
}: {
  event: Event;
  noLinks?: boolean;
  noPrint?: boolean;
  max?: number;
}) => {
  // console.log("event :", event);
  const { board } = useBoard();
  const isRef = useIsBoardReferee();
  const showClubs = getShowClubs(event.members as Member[]);
  const showFlags = getShowCountries(event.members as Member[]);
  if (!board) return <></>;
  return (
    <Card>
      <Stack>
        <Table>
          <Table.Tbody>
            {event.results &&
              event.results.map((r, index) => {
                if (max && index >= max) return <></>;
                const retired = event.withdrawals.indexOf(r) > -1;
                return (
                  <ResultRow
                    key={index}
                    position={index + 1}
                    event={event}
                    memberId={r}
                    retired={retired}
                    noLinks={noLinks}
                    showClub={showClubs}
                    showFlag={showFlags}
                  />
                );
              })}
          </Table.Tbody>
        </Table>
        {isRef && !noPrint && (
          <Group justify="right">
            <PrintResults board={board} event={event} showFlags showClubs />
          </Group>
        )}
      </Stack>
    </Card>
  );
};
export default Results;
